import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Dropdown, DropdownItem, Icon, Image, Label, Menu } from 'semantic-ui-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useStore } from '../stores/store';
import { observer } from 'mobx-react-lite';
import { languageOptions } from '../common/options/languageOptions';
import { useTranslation } from 'react-i18next';

interface NavBarProps {
    onHeightChange?: (height: number) => void;
}

export default observer(function NavBar({ onHeightChange }: NavBarProps) {
    const { userStore: { user, logout, setLanguage }, messageStore } = useStore();
    const { i18n, t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [isMobile, setIsMobile] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const navbarRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const checkIfMobile = () => {
            const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            const isSmallScreen = window.innerWidth <= 1024;
            setIsMobile(isTouchDevice && isSmallScreen);
        };

        checkIfMobile();
        window.addEventListener('resize', checkIfMobile);

        return () => window.removeEventListener('resize', checkIfMobile);
    }, []);

    const handleLanguageChange = (value: string) => {
        i18n.changeLanguage(value);
        setLanguage(value);
    };

    const getCurrentLanguage = () => {
        return languageOptions.find(lang => lang.value === i18n.language) || languageOptions[0];
    };

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const updateNavHeight = useCallback(() => {
        if (!navbarRef.current) return;
        const height = navbarRef.current.getBoundingClientRect().height;

        if (isMobile) {
            document.body.style.paddingTop = `${height}px`;
            document.documentElement.style.setProperty('--navbar-height', `${height}px`);
        } else {
            document.body.style.paddingTop = '';
            document.documentElement.style.removeProperty('--navbar-height');
        }

        onHeightChange?.(height);
    }, [isMobile, onHeightChange]);

    useEffect(() => {
        if (!isMobile) {
            document.body.style.paddingTop = '';
            document.body.style.paddingBottom = '';
            document.documentElement.style.removeProperty('--navbar-height');
            onHeightChange?.(0);
            return;
        }

        const initialTimer = setTimeout(updateNavHeight, 50);

        const observer = new ResizeObserver(() => {
            updateNavHeight();
        });

        if (navbarRef.current) {
            observer.observe(navbarRef.current);
        }

        return () => {
            clearTimeout(initialTimer);
            observer.disconnect();
            document.body.style.paddingTop = '';
            document.body.style.paddingBottom = '';
            document.documentElement.style.removeProperty('--navbar-height');
        };
    }, [isMobile, updateNavHeight]);

    const desktopGradient = 'linear-gradient(135deg, rgb(24,42,115) 0%, rgb(33,138,174) 69%, rgb(32,167,172) 89%)';

    const mobileColors = {
        text: '#ffffff',
        textSecondary: 'rgba(255,255,255,0.8)',
        border: 'rgba(255,255,255,0.15)',
        activeBg: 'rgba(255,255,255,0.15)',
        iconDefault: 'rgba(255,255,255,0.7)',
        iconActive: '#ffffff',
        primary: '#32b5c4',
    };

    // Helper function to check if a path is active
    const isActivePath = (path: string) => {
        // Za ulogovanog korisnika, Home je zapravo /playground
        if (path === '/') {
            return location.pathname === '/playground';
        }
        return location.pathname === path;
    };

    // Desktop version
    if (!isMobile) {
        return (
            <>
                <Menu
                    style={{
                        zIndex: 5000,
                        borderRadius: 0,
                        marginBottom: 0,
                        minHeight: '52px'
                    }}
                    inverted
                    fixed='top'
                    fluid
                >
                    {/* Home link - za ulogovane ide na /playground */}
                    <Menu.Item 
                        active={location.pathname === '/playground'}
                        as={Link}
                        to="/playground"
                        header
                    >
                        <Icon name='home' style={{ marginRight: '10px' }} />
                        {t('nav.home')}
                    </Menu.Item>

                    <Menu.Item 
                        active={location.pathname === '/games'}
                        as={Link}
                        to="/games"
                        header
                    >
                        <Icon name='users' style={{ marginRight: '10px' }} />
                        {t('nav.games')}
                    </Menu.Item>

                    <Menu.Item>
                        <Button
                            as={Link}
                            to="/createGame"
                            positive
                            content={t('nav.createGame')}
                            size='small'
                        />
                    </Menu.Item>

                    <Menu.Item 
                        active={location.pathname === '/teams'}
                        as={Link}
                        to="/teams"
                        header
                    >
                        <Icon name='shield' style={{ marginRight: '10px' }} />
                        {t('nav.teams')}
                    </Menu.Item>

                    <Menu.Item 
                        active={location.pathname === '/tournaments'}
                        as={Link}
                        to="/tournaments"
                    >
                        <Icon name="trophy" />
                        {t('nav.tournaments')}
                    </Menu.Item>

                    <Menu.Item 
                        active={location.pathname === '/friends'}
                        as={Link}
                        to="/friends"
                        header
                    >
                        <Icon name='user plus' style={{ marginRight: '10px' }} />
                        {t('nav.findFriends')}
                    </Menu.Item>

                    <Menu.Item 
                        active={location.pathname === '/messages'}
                        as={Link}
                        to="/messages"
                        header
                    >
                        <Icon name='comments' style={{ marginRight: '10px' }} />
                        {t('nav.messages')}
                        {messageStore.totalUnreadCount > 0 && (
                            <Label color='red' circular style={{ marginLeft: '8px' }}>
                                {messageStore.totalUnreadCount > 99 ? '99+' : messageStore.totalUnreadCount}
                            </Label>
                        )}
                    </Menu.Item>

                    <Menu.Menu position='right'>
                        <Menu.Item 
                            active={location.pathname === '/venues'}
                            as={Link}
                            to="/venues"
                            header
                        >
                            <Icon name='building' style={{ marginRight: '10px' }} />
                            {t('nav.forVenues')}
                        </Menu.Item>

                        <Menu.Item>
                            <Dropdown
                                trigger={
                                    <span style={{ cursor: 'pointer' }}>
                                        <i className={`${getCurrentLanguage().flag} flag`} />
                                        <span style={{ marginLeft: '8px' }}>
                                            {getCurrentLanguage().text.split(' ')[0]}
                                        </span>
                                    </span>
                                }
                                options={languageOptions}
                                pointing='top right'
                                onChange={(_, data) => handleLanguageChange(data.value as string)}
                                value={i18n.language}
                            />
                        </Menu.Item>

                        <Menu.Item>
                            <Image src={user?.image || '/assets/user.png'} avatar spaced='right' />
                            <Dropdown pointing='top left' text={user?.fullName}>
                                <Dropdown.Menu>
                                    <DropdownItem
                                        as={Link}
                                        to={`/profiles/${user?.userID}`}
                                        text={t('nav.myProfile')}
                                        icon='user'
                                    />
                                    <DropdownItem onClick={handleLogout} text={t('nav.logout')} icon='power' />
                                </Dropdown.Menu>
                            </Dropdown>
                        </Menu.Item>
                    </Menu.Menu>
                </Menu>
                
                <style>{`
                    .ui.inverted.menu .active.item {
                        background: rgba(255, 255, 255, 0.2) !important;
                        color: #ffffff !important;
                    }
                    
                    .ui.inverted.menu .active.item:hover {
                        background: rgba(255, 255, 255, 0.3) !important;
                    }
                `}</style>
            </>
        );
    }

    // Mobile version
    return (
        <>
            <div
                ref={navbarRef}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 5000,
                    background: desktopGradient,
                    borderBottom: `1px solid ${mobileColors.border}`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
            >
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    borderBottom: `1px solid ${mobileColors.border}`,
                }}>
                    <Link 
                        to="/playground" 
                        style={{ 
                            textDecoration: 'none', 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '8px',
                            color: location.pathname === '/playground' ? mobileColors.iconActive : mobileColors.text
                        }}
                    >
                        <span style={{ fontWeight: 'bold', fontSize: '1em' }}>
                            Sportizan
                        </span>
                    </Link>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Link 
                            to="/messages" 
                            style={{
                                position: 'relative',
                                color: location.pathname === '/messages' ? mobileColors.iconActive : mobileColors.iconDefault
                            }}
                        >
                            <Icon name='comments' size='large' />
                            {messageStore.totalUnreadCount > 0 && (
                                <Label
                                    color='red'
                                    circular
                                    size='tiny'
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-10px',
                                        minWidth: '16px',
                                        height: '16px',
                                        padding: '0 4px',
                                        fontSize: '9px',
                                        lineHeight: '16px',
                                    }}
                                >
                                    {messageStore.totalUnreadCount > 99 ? '99+' : messageStore.totalUnreadCount}
                                </Label>
                            )}
                        </Link>

                        <Link 
                            to="/friends" 
                            style={{
                                color: location.pathname === '/friends' ? mobileColors.iconActive : mobileColors.iconDefault
                            }}
                        >
                            <Icon name='user plus' size='large' />
                        </Link>

                        <Dropdown
                            trigger={
                                <Button basic inverted size='mini' style={{ padding: '6px 8px' }}>
                                    <i className={`${getCurrentLanguage().flag} flag`} />
                                </Button>
                            }
                            options={languageOptions}
                            pointing='top right'
                            onChange={(_, data) => handleLanguageChange(data.value as string)}
                            value={i18n.language}
                        />

                        <Dropdown
                            trigger={
                                <Image
                                    src={user?.image || '/assets/user.png'}
                                    avatar
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        cursor: 'pointer',
                                        border: `2px solid ${mobileColors.primary}`,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}
                                />
                            }
                            pointing='top right'
                        >
                            <Dropdown.Menu>
                                <DropdownItem
                                    as={Link}
                                    to={`/profiles/${user?.userID}`}
                                    text={t('nav.myProfile')}
                                    icon='user'
                                />
                                <DropdownItem onClick={handleLogout} text={t('nav.logout')} icon='power' />
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>

                <div
                    ref={scrollContainerRef}
                    style={{
                        display: 'flex',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'thin',
                        padding: '8px 12px',
                        gap: '6px',
                        whiteSpace: 'nowrap',
                        msOverflowStyle: 'none',
                    }}
                    className="hide-scrollbar"
                >
                    <Link to="/playground" style={{ textDecoration: 'none' }}>
                        <Button
                            inverted
                            size='small'
                            style={{
                                borderRadius: '20px',
                                padding: '6px 14px',
                                backgroundColor: location.pathname === '/playground' ? mobileColors.activeBg : 'transparent',
                                color: location.pathname === '/playground' ? mobileColors.iconActive : mobileColors.textSecondary,
                                border: `1px solid ${location.pathname === '/playground' ? mobileColors.iconActive : mobileColors.border}`,
                            }}
                        >
                            <Icon name='home' />
                            {t('nav.home')}
                        </Button>
                    </Link>

                    <Link to="/games" style={{ textDecoration: 'none' }}>
                        <Button
                            inverted
                            size='small'
                            style={{
                                borderRadius: '20px',
                                padding: '6px 14px',
                                backgroundColor: location.pathname === '/games' ? mobileColors.activeBg : 'transparent',
                                color: location.pathname === '/games' ? mobileColors.iconActive : mobileColors.textSecondary,
                                border: `1px solid ${location.pathname === '/games' ? mobileColors.iconActive : mobileColors.border}`,
                            }}
                        >
                            <Icon name='users' />
                            {t('nav.games')}
                        </Button>
                    </Link>

                    <Link to="/createGame" style={{ textDecoration: 'none' }}>
                        <Button
                            style={{
                                borderRadius: '20px',
                                padding: '6px 14px',
                                background: 'linear-gradient(135deg, #21ba45 0%, #16ab39 100%)',
                                color: 'white',
                                fontWeight: 'bold',
                                border: 'none',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                            }}
                        >
                            <Icon name='plus' style={{ color: 'white' }} />
                            {t('nav.createGame')}
                        </Button>
                    </Link>

                    <Link to="/teams" style={{ textDecoration: 'none' }}>
                        <Button
                            inverted
                            size='small'
                            style={{
                                borderRadius: '20px',
                                padding: '6px 14px',
                                backgroundColor: location.pathname === '/teams' ? mobileColors.activeBg : 'transparent',
                                color: location.pathname === '/teams' ? mobileColors.iconActive : mobileColors.textSecondary,
                                border: `1px solid ${location.pathname === '/teams' ? mobileColors.iconActive : mobileColors.border}`,
                            }}
                        >
                            <Icon name='shield' />
                            {t('nav.teams')}
                        </Button>
                    </Link>

                    <Link to="/tournaments" style={{ textDecoration: 'none' }}>
                        <Button
                            inverted
                            size='small'
                            style={{
                                borderRadius: '20px',
                                padding: '6px 14px',
                                backgroundColor: location.pathname === '/tournaments' ? mobileColors.activeBg : 'transparent',
                                color: location.pathname === '/tournaments' ? mobileColors.iconActive : mobileColors.textSecondary,
                                border: `1px solid ${location.pathname === '/tournaments' ? mobileColors.iconActive : mobileColors.border}`,
                            }}
                        >
                            <Icon name='trophy' />
                            {t('nav.tournaments')}
                        </Button>
                    </Link>

                    <Link to="/venues" style={{ textDecoration: 'none' }}>
                        <Button
                            inverted
                            size='small'
                            style={{
                                borderRadius: '20px',
                                padding: '6px 14px',
                                backgroundColor: location.pathname === '/venues' ? mobileColors.activeBg : 'transparent',
                                color: location.pathname === '/venues' ? mobileColors.iconActive : mobileColors.textSecondary,
                                border: `1px solid ${location.pathname === '/venues' ? mobileColors.iconActive : mobileColors.border}`,
                            }}
                        >
                            <Icon name='building' />
                            {t('nav.forVenues')}
                        </Button>
                    </Link>
                </div>
            </div>

            <style>{`
                .hide-scrollbar::-webkit-scrollbar {
                    display: none;
                }
                .hide-scrollbar {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }

                html {
                    scroll-padding-top: var(--navbar-height, 0px);
                }
            `}</style>
        </>
    );
});